import { usePermission } from "../../auth/access";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FeedIcon from "@mui/icons-material/DynamicFeedOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOffOutlined";
import LoginIcon from "@mui/icons-material/Login";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import { useCallback, useEffect, useState } from "react";
import {
  CONTENT_OWNER_KIND_OPTIONS,
  foyerApi,
  type CompanySummary,
  type ContentOwnerRecord,
  type ContentSupplierRow,
} from "../../http/foyer";
import { tokens } from "../../theme";
import { friendlyError } from "../../utils/apiError";
import DangerConfirmDialog from "../DangerConfirmDialog";
import { describeError, useToast } from "../toast";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../i18n/format";
import PurchasePortalDialog from "../portal/PurchasePortalDialog";
import {
  AddSupplierDialog,
  EditFeedDialog,
  EditLoginDialog,
} from "../contentFeeds/ContentFeedDialogs";

/**
 * A company's settings (Sigge, 2026-10-19): its content feeds and, for a
 * Content-owner company, its login profile and purchase portal - the things
 * that used to live on the Content providers page.
 *
 * - **Feeds** belong to any company type (a hotel can hold its own content),
 *   several per company (Tigrar is four labels). A feed with a company is
 *   governed HERE, so its MRSS is edited here, and the Content feeds list
 *   links back.
 * - **Connecting an existing feed** is the only way an already-catalogued feed
 *   gets a company, so it is offered next to creating one. Attaching moves the
 *   ownership of that feed's titles; detaching takes it back - each is its own
 *   deliberate step, and a feed held by another company must be detached there
 *   first.
 * - **The login, storefront and money** are the company's, not a feed's: one
 *   profile, one purchase portal, whatever number of feeds.
 */
export default function CompanySettingsDialog({
  company,
  onClose,
}: {
  company: CompanySummary;
  onClose: () => void;
}) {
  const ownerCompany = company.type === "CONTENT_OWNER";
  // Two hooks, both always called: `&&` would skip the second one.
  const canDistribute = usePermission(
    "distribution/suppliers",
    "administrate",
    "nenda"
  );
  const canTrust = usePermission("supplier-trust", "administrate", "nenda");
  const canManageFeeds = canDistribute && canTrust;

  const [feeds, setFeeds] = useState<ContentSupplierRow[]>([]);
  const [unattached, setUnattached] = useState<ContentSupplierRow[]>([]);
  const [owner, setOwner] = useState<ContentOwnerRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const format = useFormat();
  const { t } = useTranslation(["users", "common", "content"]);
  const [busy, setBusy] = useState(false);
  const [connectId, setConnectId] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingFeed, setEditingFeed] = useState<ContentSupplierRow | null>(
    null
  );
  const [detaching, setDetaching] = useState<ContentSupplierRow | null>(null);
  const [editingLogin, setEditingLogin] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, owners] = await Promise.all([
        canManageFeeds
          ? foyerApi.listContentSuppliers().then((r) => r.suppliers)
          : Promise.resolve([] as ContentSupplierRow[]),
        ownerCompany
          ? foyerApi.listContentOwners()
          : Promise.resolve([] as ContentOwnerRecord[]),
      ]);
      setFeeds(list.filter((s) => s.company?.id === company.id));
      setUnattached(list.filter((s) => !s.company));
      setOwner(owners.find((o) => o.id === company.id) ?? null);
      setError(null);
    } catch (e) {
      setError(friendlyError(t, e));
    } finally {
      setLoading(false);
    }
  }, [canManageFeeds, ownerCompany, company.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function connect() {
    if (!connectId) return;
    setBusy(true);
    setError(null);
    const feedName =
      unattached.find((f) => f.id === connectId)?.name ??
      t("companiesPage.create.feedFallbackName");
    try {
      await foyerApi.setContentSupplierCompany(connectId, company.id);
      setConnectId("");
      toast.success(
        t("companySettingsDialog.feedConnected", {
          feed: feedName,
          company: company.name,
        })
      );
      await load();
    } catch (e) {
      const err = e as { body?: { hint?: string } };
      toast.error(
        err.body?.hint
          ? t("companySettingsDialog.connectFailedHint", {
              feed: feedName,
              company: company.name,
              hint: err.body.hint,
            })
          : describeError(t, "connectFeedToCompany", e, {
              feed: feedName,
              company: company.name,
            })
      );
    } finally {
      setBusy(false);
    }
  }

  async function detach() {
    if (!detaching) return;
    setBusy(true);
    setError(null);
    try {
      await foyerApi.setContentSupplierCompany(detaching.id, null);
      toast.success(
        t("companySettingsDialog.feedDetached", {
          feed: detaching.name,
          company: company.name,
        })
      );
      setDetaching(null);
      await load();
    } catch (e) {
      toast.error(
        describeError(t, "detachFeedFromCompany", e, {
          feed: detaching.name,
          company: company.name,
        })
      );
    } finally {
      setBusy(false);
    }
  }

  const kindLabel = owner?.kind
    ? CONTENT_OWNER_KIND_OPTIONS.some((k) => k.value === owner.kind)
      ? t(`content:contentFeedDialogs.ownerKind.${owner.kind}`)
      : owner.kind
    : null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("companySettingsDialog.title", { name: company.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : null}

          {!loading && ownerCompany ? (
            <Box data-testid="company-owner-section">
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "fontWeightSemibold", mb: 1 }}
              >
                {t("companySettingsDialog.loginHeading")}
              </Typography>
              {owner ? (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color={owner.enabled ? "success" : "default"}
                      label={
                        owner.enabled
                          ? t("companySettingsDialog.loginEnabled")
                          : t("companySettingsDialog.loginDisabled")
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`/${owner.slug}`}
                    />
                    {kindLabel ? (
                      <Chip size="small" variant="outlined" label={kindLabel} />
                    ) : (
                      <Chip
                        size="small"
                        color="warning"
                        label={t("companySettingsDialog.kindNotSet")}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        owner.revenueShareBps === null
                          ? t("companySettingsDialog.revenueShareNotAgreed")
                          : t("companySettingsDialog.revenueShare", {
                              share: format.number(
                                owner.revenueShareBps / 10000,
                                { style: "percent", maximumFractionDigits: 2 }
                              ),
                            })
                      }
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t("companySettingsDialog.loginHelp")}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<LoginIcon />}
                      onClick={() => setEditingLogin(true)}
                      data-testid="company-edit-login"
                    >
                      {t("companySettingsDialog.editLogin")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<StorefrontIcon />}
                      onClick={() => setPortalOpen(true)}
                      data-testid="company-purchase-portal"
                    >
                      {t("companySettingsDialog.purchasePortal")}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Alert severity="warning">
                  {t("companySettingsDialog.noLoginProfile")}
                </Alert>
              )}
            </Box>
          ) : null}

          {!loading && ownerCompany ? <Divider /> : null}

          {!loading ? (
            <Box data-testid="company-feeds-section">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "fontWeightSemibold" }}
                >
                  {t("companySettingsDialog.feedsHeading")}
                </Typography>
                {canManageFeeds ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAdding(true)}
                    disabled={busy}
                    data-testid="company-feed-new"
                  >
                    {t("companySettingsDialog.newFeed")}
                  </Button>
                ) : null}
              </Stack>

              {!canManageFeeds ? (
                <Alert severity="info">
                  {t("companySettingsDialog.needsRights")}
                </Alert>
              ) : (
                <>
                  {feeds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("companySettingsDialog.noFeeds")}{" "}
                      {ownerCompany
                        ? t("companySettingsDialog.noFeedsOwner")
                        : t("companySettingsDialog.noFeedsCompany")}
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("common:labels.feed")}</TableCell>
                          <TableCell>{t("common:labels.id")}</TableCell>
                          <TableCell>
                            {t("companySettingsDialog.columns.mrss")}
                          </TableCell>
                          <TableCell>{t("common:labels.trusted")}</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {feeds.map((feed) => (
                          <TableRow
                            key={feed.id}
                            data-testid={`company-feed-${feed.id}`}
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <FeedIcon
                                  fontSize="small"
                                  sx={{ color: tokens.inkMuted }}
                                  aria-hidden
                                />
                                <Typography variant="body2">
                                  {feed.name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "fontFamilyMono",
                                  color: tokens.inkMuted,
                                }}
                              >
                                {feed.id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  feed.mrssFeedUrl
                                    ? t("companySettingsDialog.hasMrss")
                                    : t("companySettingsDialog.noMrss")
                                }
                              >
                                <IconButton
                                  size="small"
                                  color={
                                    feed.mrssFeedUrl ? "primary" : undefined
                                  }
                                  sx={
                                    feed.mrssFeedUrl
                                      ? undefined
                                      : { color: tokens.inkFaint }
                                  }
                                  onClick={() => setEditingFeed(feed)}
                                  aria-label={t(
                                    "companySettingsDialog.mrssFor",
                                    { name: feed.name }
                                  )}
                                  data-testid={`company-feed-mrss-${feed.id}`}
                                >
                                  <RssFeedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {feed.trusted
                                  ? t("common:buttons.yes")
                                  : t("common:buttons.no")}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip
                                title={t("companySettingsDialog.detachTooltip")}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => setDetaching(feed)}
                                  disabled={busy}
                                  aria-label={t(
                                    "companySettingsDialog.detachNamed",
                                    { name: feed.name }
                                  )}
                                  data-testid={`company-feed-detach-${feed.id}`}
                                >
                                  <LinkOffIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ sm: "center" }}
                    sx={{ mt: 2 }}
                  >
                    <TextField
                      select
                      size="small"
                      label={t("companySettingsDialog.connectLabel")}
                      value={connectId}
                      onChange={(e) => setConnectId(e.target.value)}
                      sx={{ minWidth: 260, flex: 1 }}
                      helperText={t("companiesPage.create.connectHelp")}
                      inputProps={{
                        "data-testid": "company-feed-connect-select",
                      }}
                    >
                      {unattached.length === 0 ? (
                        <MenuItem value="" disabled>
                          {t("companiesPage.create.noUnattachedFeeds")}
                        </MenuItem>
                      ) : null}
                      {unattached.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              ml: 1,
                              color: tokens.inkMuted,
                              fontFamily: "fontFamilyMono",
                            }}
                          >
                            {s.id}
                          </Typography>
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      startIcon={<LinkIcon />}
                      onClick={() => void connect()}
                      disabled={busy || !connectId}
                      data-testid="company-feed-connect"
                      sx={{ alignSelf: { sm: "flex-start" }, mt: { sm: 0.5 } }}
                    >
                      {t("companySettingsDialog.connect")}
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.close")}</Button>
      </DialogActions>

      {adding ? (
        <AddSupplierDialog
          company={{ id: company.id, name: company.name }}
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await load();
          }}
        />
      ) : null}

      {editingFeed ? (
        <EditFeedDialog
          supplier={editingFeed}
          onClose={() => setEditingFeed(null)}
          onSaved={async () => {
            setEditingFeed(null);
            await load();
          }}
        />
      ) : null}

      {editingLogin && owner ? (
        <EditLoginDialog
          owner={owner}
          onClose={() => setEditingLogin(false)}
          onSaved={async () => {
            setEditingLogin(false);
            await load();
          }}
        />
      ) : null}

      {portalOpen ? (
        <PurchasePortalDialog
          ownerId={company.id}
          ownerName={company.name}
          onClose={() => setPortalOpen(false)}
        />
      ) : null}

      <DangerConfirmDialog
        open={detaching !== null}
        title={t("companySettingsDialog.detachTitle")}
        entityName={detaching?.name ?? ""}
        body={
          ownerCompany
            ? t("companySettingsDialog.detachBodyOwner", {
                company: company.name,
              })
            : t("companySettingsDialog.detachBodyCompany")
        }
        busy={busy}
        onConfirm={() => void detach()}
        onCancel={() => setDetaching(null)}
      />
    </Dialog>
  );
}
